import React, { useState, useContext } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
// import evLogo from "../../assets/images/ev-logo.jpg";
import ecLogo from "../../assets/images/delivery.png";
import eCheckLogo from "../../assets/images/delivery.png";
import dealerlineLogo from "../../assets/images/dl_logo.png";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  Grid,
  Avatar,
  Menu,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../context/Auth.context";
import { Context as DrawerContext } from "../../context/Drawer.context";
import { TYPE, capitalizeFirstLetter, drawerItems } from "../../utils/Utils";
import { useAppContext } from "../../context/App.context";

import HocPopup from "../../hoc/hocPopup";
import LogoutDialog from "./dialog-body/logout.dialog";
import { useEffect } from "react";
import InformationDialog from "../base/dialog-body/Information.dialog";
import AppButton from "../base/AppButton.component";

// Enhanced print styles with better table visibility
const printStyles = `
  @media print {
    @page {
      size: landscape;
      margin: 0.5in;
    }
    
    /* Hide non-printable elements */
    .MuiDrawer-root,
    .MuiAppBar-root,
    .MuiButton-root:not(.print-button),
    .no-print,
    .MuiIconButton-root,
    .MuiMenu-root,
    .MuiSelect-root,
    .MuiFormControl-root,
    .MuiAvatar-root,
    [aria-label="open drawer"],
    [aria-label="logout"] {
      display: none !important;
    }
    
    /* Ensure main content is fully visible */
    main {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 20px !important;
      overflow: visible !important;
      height: auto !important;
    }
    
    /* Reset body styles for printing */
    body {
      margin: 0;
      padding: 0;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    /* Table styles - ensure all data is visible */
    table, 
    .MuiTable-root,
    .MuiTableContainer-root {
      width: 100% !important;
      border-collapse: collapse !important;
      page-break-inside: auto !important;
      font-size: 10pt;
      display: table !important;
    }
    
    /* Table row handling */
    tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      display: table-row !important;
    }
    
    /* Table cell handling - prevent data hiding */
    td, th {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      padding: 8px !important;
      border: 1px solid #ddd !important;
      display: table-cell !important;
      vertical-align: top !important;
    }
    
    /* Ensure all content is visible */
    * {
      visibility: visible !important;
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
    }
    
    /* Headers repeat on each page */
    thead {
      display: table-header-group !important;
    }
    
    tbody {
      display: table-row-group !important;
    }
    
    /* Table header styling */
    th {
      background-color: #f2f2f2 !important;
      font-weight: bold !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* Page numbers */
    @page {
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-size: 8pt;
      }
    }
  }
`;

const drawerWidth = 300;

// Shared style for all appbar logos: fixed height, auto width, small padding
// so the Toolbar/AppBar always renders at the 48px minHeight regardless of
// each dealership logo's native aspect ratio.
const appbarLogoStyle = {
  height: "40px",
  width: "auto",
  maxHeight: "40px",
  objectFit: "contain",
  padding: "2px 8px",
};

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(9)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(10)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  minHeight: "48px !important",
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  background: "#fff",
  boxShadow: "none",
}));

const Drawer = styled(MuiDrawer, {
  // shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const MiniDrawer = ({ dialog }) => {
  const { state: drawerStatus } = useContext(DrawerContext);
  const navigate = useNavigate();
  const auth = useAuth();
  const app = useAppContext();
  const theme = useTheme();
  let location = useLocation();
  const [open, setOpen] = useState(drawerStatus.webDrawerStatus);
  const [mobileOpen, setMobileOpen] = useState(drawerStatus.mobileDrawerStatus);
  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = Boolean(anchorEl);
  const navLink = (link) => {
    switch (link) {
      case "/":
      case "/manageUsers":
      case "/insurance":
      case "/transactions":
      case "/reports": 
        navigate(link);
        break;
      default:
        break;
    }
  };

  const logout = () => {
    auth.logout();
    app.removeAll();
    navigate("/login");
  };
  console.log(
    "DEALERCoDE",
    auth.user.dealership_name,
    auth.user.dealership_code,
  );

  const [dialogClose, setDialogClose] = useState(false);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuHandler = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const [manager, setManager] = useState("");

  const switchDealership = async (dealershipName) => {
    setManager(dealershipName);
    let dealership = auth?.user?.associateDetails.filter(
      (dealership) => dealership.dealership_name === dealershipName,
    );
    console.log("Fdealer", dealership);

    await auth.login({
      ...auth.user,
      dealership_id: dealership[0].dealership_id,
      dealership_name: dealership[0].dealership_name,
      brand_name: dealership[0].brand_name,
      dealership_code: dealership[0].dealership_code,
      payload: dealership[0].payload_preference,
      dealership_logo: dealership[0].dealership_logo,
      dealershipServices: dealership?.[0]?.dealershipServices,
    });
    // window.location.reload();
  };

  // Function to ensure all table data is visible
  const prepareTablesForPrint = () => {
    const tables = document.querySelectorAll(
      "table, .MuiTable-root, .MuiTableContainer-root",
    );
    tables.forEach((table) => {
      // Remove any height restrictions
      table.style.height = "auto";
      table.style.maxHeight = "none";
      table.style.overflow = "visible";

      // Ensure all rows are visible
      const rows = table.querySelectorAll("tr");
      rows.forEach((row) => {
        row.style.height = "auto";
        row.style.maxHeight = "none";
        row.style.display = "table-row";
        row.style.overflow = "visible";
      });

      // Ensure all cells are visible
      const cells = table.querySelectorAll("td, th");
      cells.forEach((cell) => {
        cell.style.height = "auto";
        cell.style.maxHeight = "none";
        cell.style.overflow = "visible";
        cell.style.whiteSpace = "normal";
        cell.style.wordWrap = "break-word";
        cell.style.display = "table-cell";
      });
    });
  };

  // Print handler function
  const handlePrint = () => {
    // Add print styles to document if not already present
    if (!document.getElementById("print-styles")) {
      const style = document.createElement("style");
      style.id = "print-styles";
      style.innerHTML = printStyles;
      document.head.appendChild(style);
    }

    // Prepare tables for printing
    prepareTablesForPrint();

    // Trigger browser print dialog
    setTimeout(() => {
      window.print();
    }, 100);

    // Remove styles after printing
    setTimeout(() => {
      const printStyle = document.getElementById("print-styles");
      if (printStyle) {
        printStyle.remove();
      }
    }, 1000);
  };

  return (
    <Box sx={{ display: "flex", overflow: "hidden", flexGrow: 1 }}>
      <CssBaseline />
      {/* Add print styles to document head */}
      <style>{printStyles}</style>

      {/* AppBar: reverted to the original two-way split. Admin and manager
          get the full header (drawer-toggle icons, dealership/group
          selector, print button). Employee gets a lighter header, but now
          WITH its own drawer-toggle icons added below so employees can
          still open/close their sidebar (Home/Reports). */}
      {auth.user && auth.user.loggedInAs !== "employee" ? (
        <AppBar
          position="fixed"
          open={open}
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRight: "0px",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", py: 0 }}>
            <IconButton
              color="primary"
              aria-label="open drawer"
              onClick={() => setOpen(true)}
              edge="start"
              sx={{
                marginRight: 2,
                marginLeft: 0,
                display: {
                  xs: "none",
                  sm: "block",
                  md: "block",
                },
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>

            <IconButton
              color="primary"
              aria-label="open drawer"
              onClick={() => setMobileOpen(!mobileOpen)}
              edge="start"
              sx={{
                marginRight: 2,
                marginLeft: 0,
                display: { xs: "block", sm: "none", md: "none" },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Grid
              container
              justifyContent={"space-between"}
              alignItems="center"
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                // alignItems="center"
                // gap={10}
              >
                {auth?.user?.loggedInAs === "admin" ? (
                  <>
                    <img
                      src={eCheckLogo}
                      alt="logo"
                      style={appbarLogoStyle}
                    />
                  </>
                ) : (
                  <>
                    <Box p={2} width={350} display="flex" alignItems="center">
                      {location.pathname === "/groupDashboard" ? (
                        <>
                          <img
                            src={dealerlineLogo}
                            alt="dealerline logo"
                            style={appbarLogoStyle}
                          />
                        </>
                      ) : auth?.user?.dealership_logo ? (
                        <>
                          <img
                            src={auth?.user?.dealership_logo}
                            alt="logo"
                            style={appbarLogoStyle}
                          />
                        </>
                      ) : (
                        <CircularProgress color="primary" />
                      )}
                    </Box>
                  </>
                )}
              </Box>

              <Grid item sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                {/* Print Button - Only show on Group Dashboard */}
                {auth?.user?.loggedInAs !== "admin" && 
                 location.pathname === "/groupDashboard" && (
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    size="medium"
                    className="print-button"
                    sx={{
                      display: { xs: "none", sm: "flex" },
                      backgroundColor: "white",
                      px: 3,
                      border: "2px solid",
                      borderColor: "primary.main",
                      "@media print": {
                        display: "none !important",
                      },
                    }}
                  >
                    Print
                  </Button>
                )}

                {auth?.user?.loggedInAs !== "admin" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <FormControl
                      variant="standard"
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "none",
                        },
                        "& .MuiSelect-select": {
                          border: "none",
                          outline: "none",
                        },
                      }}
                    >
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={
                          location.pathname === "/groupDashboard"
                            ? "GROUP_DASHBOARD"
                            : auth?.user?.dealership_name
                        }
                        onChange={async (event) => {
                          const value = event.target.value;
                          if (value === "GROUP_DASHBOARD") {
                            navigate("/groupDashboard");
                            return;
                          }
                          await switchDealership(value);
                          // Show refresh dialog
                          dialog({
                            open: true,
                            bodyType: TYPE.DEFAULT,
                            title: "Warning",
                            body: (
                              <InformationDialog
                                text={
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <p>
                                      You are switching dealerships. Please
                                      click the REFRESH button below
                                    </p>
                                    <AppButton
                                      sx={{ width: "40%", alignSelf: "center" }}
                                      buttonText="Refresh"
                                      size="small"
                                      variant="contained"
                                      clickHandler={() => {
                                        // Set flag to show home component, then navigate and refresh
                                        sessionStorage.setItem("navigatedToHome", "true");
                                        window.location.href = "/";
                                      }}
                                    />
                                  </div>
                                }
                              />
                            ),
                          });
                        }}
                        sx={{
                          maxHeight: "50px",
                          width: "210px",
                          fontSize: 15,
                        }}
                      >
                        {/* ✅ Show Group Dashboard only if manager + multiple dealers */}
                        {(auth?.user?.loggedInAs === "manager" ||
                          auth?.user?.loggedInAs === "employee") &&
                          auth?.user?.associateDetails?.length > 1 && (
                            <MenuItem value="GROUP_DASHBOARD">
                              Group Dashboard
                            </MenuItem>
                          )}

                        {/* Existing Dealers */}
                        {auth?.user?.associateDetails?.map(
                          (dealership, index) => (
                            <MenuItem
                              key={index}
                              value={dealership.dealership_name}
                            >
                              {dealership?.dealership_code}{" "}
                              {capitalizeFirstLetter(
                                dealership.dealership_name,
                              )}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  sx={{
                    justifyContent: "center",
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.dark,
                    }}
                  >
                    {auth.user
                      ? auth.user.first_name.charAt(0)?.toUpperCase() +
                        "" +
                        auth.user.last_name.charAt(0)?.toUpperCase()
                      : null}
                  </Avatar>
                </IconButton>
                <IconButton
                  color="error"
                  aria-label="open drawer"
                  onClick={menuHandler}
                  edge="end"
                  size="small"
                >
                  <PowerSettingsNewIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                >
                  <MenuItem onClick={logout}>Logout</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      ) : (
        <AppBar
          position="fixed"
          open={open}
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRight: "0px",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", py: 0 }}>
            {/* NEW: drawer-toggle icons for employees, mirroring the ones
                in the full AppBar above, so employees can open/close their
                sidebar (Home/Reports) instead of it being stuck open or
                inaccessible. */}
            <IconButton
              color="primary"
              aria-label="open drawer"
              onClick={() => setOpen(true)}
              edge="start"
              sx={{
                marginRight: 2,
                marginLeft: 0,
                display: {
                  xs: "none",
                  sm: "block",
                  md: "block",
                },
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>

            <IconButton
              color="primary"
              aria-label="open drawer"
              onClick={() => setMobileOpen(!mobileOpen)}
              edge="start"
              sx={{
                marginRight: 2,
                marginLeft: 0,
                display: { xs: "block", sm: "none", md: "none" },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Grid
              container
              justifyContent={"space-between"}
              alignItems="center"
            >
              <Box
                display={"flex"}
                flexDirection={"row"}
                alignItems="center"
                gap={2}
                width={350}
              >
                {location.pathname === "/groupDashboard" ? (
                  <>
                    <img
                      src={dealerlineLogo}
                      alt="dealerline logo"
                      style={{ ...appbarLogoStyle, marginLeft: "24px" }}
                    />
                  </>
                ) : auth?.user?.dealership_logo ? (
                  <>
                    <img
                      src={auth?.user?.dealership_logo}
                      alt="logo"
                      style={{ ...appbarLogoStyle, marginLeft: "24px" }}
                    />
                  </>
                ) : (
                  <CircularProgress color="primary" />
                )}
              </Box>

              <Grid item sx={{ display: "flex", alignItems: "center" }}>
                {/* Print Button for Employee - Uncomment if needed */}
                {/* {auth?.user?.loggedInAs !== "admin" && (
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    size="medium"
                    className="print-button"
                    sx={{
                      mr: 2,
                      display: { xs: "none", sm: "flex" },
                      '@media print': {
                        display: 'none !important'
                      }
                    }}
                  >
                    Print
                  </Button>
                )} */}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mr: 2,
                  }}
                >
                  <FormControl
                    variant="standard"
                    sx={{
                      "& .MuiInput-root": {
                        borderBottom: "none",
                      },
                      "& .MuiSelect-select": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  >
                    <Select
                      labelId="demo-simple-select-label-emp"
                      id="demo-simple-select-emp"
                      value={
                        location.pathname === "/groupDashboard"
                          ? "GROUP_DASHBOARD"
                          : auth?.user?.dealership_name
                      }
                      onChange={async (event) => {
                        const value = event.target.value;
                        if (value === "GROUP_DASHBOARD") {
                          navigate("/groupDashboard");
                          return;
                        }
                        await switchDealership(value);
                        dialog({
                          open: true,
                          bodyType: TYPE.DEFAULT,
                          title: "Warning",
                          body: (
                            <InformationDialog
                              text={
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                  }}
                                >
                                  <p>
                                    You are switching dealerships. Please click
                                    the REFRESH button below
                                  </p>
                                  <AppButton
                                    sx={{ width: "40%", alignSelf: "center" }}
                                    buttonText="Refresh"
                                    size="small"
                                    variant="contained"
                                    clickHandler={() => {
                                      sessionStorage.setItem(
                                        "navigatedToHome",
                                        "true"
                                      );
                                      window.location.href = "/";
                                    }}
                                  />
                                </div>
                              }
                            />
                          ),
                        });
                      }}
                      sx={{
                        maxHeight: "50px",
                        width: "210px",
                        fontSize: 15,
                      }}
                    >
                      {auth?.user?.associateDetails?.length > 1 && (
                        <MenuItem value="GROUP_DASHBOARD">
                          Group Dashboard
                        </MenuItem>
                      )}

                      {auth?.user?.associateDetails?.map(
                        (dealership, index) => (
                          <MenuItem key={index} value={dealership.dealership_name}>
                            {dealership?.dealership_code}{" "}
                            {capitalizeFirstLetter(dealership.dealership_name)}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>

                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  sx={{
                    marginRight: 3,
                    justifyContent: "flex-end",
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.dark,
                    }}
                  >
                    {auth.user
                      ? auth.user.first_name.charAt(0)?.toUpperCase() +
                        "" +
                        auth.user.last_name.charAt(0)?.toUpperCase()
                      : null}
                  </Avatar>
                </IconButton>
                <IconButton
                  color="error"
                  aria-label="open drawer"
                  onClick={menuHandler}
                  edge="end"
                  size="small"
                  sx={{
                    justifyContent: "flex-end",
                  }}
                >
                  <PowerSettingsNewIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleClose}
                  MenuListProps={{
                    "aria-labelledby": "basic-button",
                  }}
                >
                  <MenuItem onClick={logout}>Logout</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer: now renders for admin, manager, AND employee — each role's
          drawerItems() call returns the correct set of links (admin gets
          Dealerships/Transaction History; manager gets the full menu;
          employee gets Home/Reports only), so gating on role identity here
          isn't needed beyond confirming a logged-in user with a known role. */}
      {auth.user &&
      (auth.user.loggedInAs === "admin" ||
        auth.user.loggedInAs === "manager" ||
        auth.user.loggedInAs === "employee") ? (
        <>
          <Drawer
            variant="permanent"
            open={open}
            PaperProps={{
              sx: {
                background: theme.palette.background.default,
                borderRight: "0px",
              },
            }}
            sx={{
              display: {
                sm: "block",
                xs: "none",
              },
            }}
          >
            <DrawerHeader>
              <IconButton onClick={() => setOpen(false)}>
                {theme.direction === "rtl" ? (
                  <ChevronRightIcon />
                ) : (
                  <ChevronLeftIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <List
              sx={{
                paddingX: "8px",
                marginTop: "40px",
              }}
            >
              {drawerItems(auth.user.loggedInAs).map((item, index) => (
                <ListItem
                  key={index}
                  sx={[
                    {
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "row",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      marginTop: "4px",
                      marginBottom: "4px",
                      justifyContent: "center",
                      cursor: "pointer",
                    },
                    {
                      backgroundColor:
                        location.pathname === item.link
                          ? theme.palette.secondary.main
                          : theme.palette.common.white,
                      "& > *": {
                        color:
                          location.pathname === item.link
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                      },
                    },
                    (theme) => ({
                      "&:hover": {
                        backgroundColor: theme.palette.secondary.main,
                        "& > *": {
                          color: theme.palette.primary.main,
                        },
                      },
                    }),
                    {
                      backgroundColor:
                        location.pathname === item.link
                          ? theme.palette.secondary.main
                          : theme.palette.background.default,
                      "& > *": {
                        color:
                          location.pathname === item.link
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                      },
                    },
                  ]}
                  onClick={() => navLink(item.link)}
                >
                  <ListItemButton
                    disableRipple
                    sx={[
                      {
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      },
                      (theme) => ({
                        "&:hover": {
                          background: "none",
                        },
                      }),
                    ]}
                  >
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        },
                        {
                          background: "none",
                          "& > *": {
                            color:
                              location.pathname === item.link
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                          },
                        },
                      ]}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      sx={[
                        { opacity: open ? 1 : 0 },
                        {
                          background: "none",
                          "& > *": {
                            color:
                              location.pathname === item.link
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                          },
                        },
                      ]}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Drawer>

          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sx={{ display: { sm: "none" } }}
            PaperProps={{
              sx: {
                background: theme.palette.background.default,
              },
            }}
          >
            <List sx={{ paddingX: "8px", marginTop: "60px" }}>
              {drawerItems(auth.user.loggedInAs).map((item, index) => (
                <ListItem
                  key={index}
                  sx={[
                    {
                      borderRadius: "8px",
                      display: "flex",
                      flexDirection: "row",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      marginTop: "4px",
                      marginBottom: "4px",
                      justifyContent: "center",
                      cursor: "pointer",
                    },
                    {
                      backgroundColor:
                        location.pathname === item.link
                          ? theme.palette.secondary.main
                          : theme.palette.common.white,
                      "& > *": {
                        color:
                          location.pathname === item.link
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                      },
                    },
                  ]}
                  onClick={() => {
                    navLink(item.link);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemButton
                    disableRipple
                    sx={[
                      {
                        minHeight: 48,
                        px: 2.5,
                      },
                      (theme) => ({
                        "&:hover": {
                          background: "none",
                        },
                      }),
                    ]}
                  >
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: "center",
                        },
                        {
                          background: "none",
                          "& > *": {
                            color:
                              location.pathname === item.link
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                          },
                        },
                      ]}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      sx={[
                        {
                          ml: 1,
                          background: "none",
                          "& > *": {
                            color:
                              location.pathname === item.link
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                          },
                        },
                      ]}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Drawer>
        </>
      ) : null}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
        }}
      >
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
};

export default HocPopup(MiniDrawer);